import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Instagram, Facebook } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useBusinessInfo } from '../context/BusinessContext';

// Custom Airbnb icon component
const AirbnbIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.001 18.275c-.208 0-.417-.052-.604-.156l-.073-.041c-1.537-.885-2.653-1.843-3.469-2.854-.949-1.176-1.447-2.415-1.447-3.594 0-1.503.558-2.682 1.562-3.312.521-.327 1.095-.49 1.713-.49.751 0 1.482.247 2.173.734.146.104.28.214.401.329.121-.115.255-.225.401-.329.691-.487 1.422-.734 2.173-.734.618 0 1.192.163 1.713.49 1.004.63 1.562 1.809 1.562 3.312 0 1.179-.498 2.418-1.447 3.594-.816 1.011-1.932 1.969-3.469 2.854l-.073.041c-.187.104-.396.156-.604.156h-.012zm-2.318-8.869c-.391 0-.751.104-1.068.307-.683.432-1.047 1.236-1.047 2.317 0 .875.365 1.816 1.12 2.752.688.854 1.64 1.672 2.912 2.433l.401.229.401-.229c1.272-.761 2.224-1.579 2.912-2.433.755-.936 1.12-1.877 1.12-2.752 0-1.081-.364-1.885-1.047-2.317-.317-.203-.677-.307-1.068-.307-.505 0-1.005.172-1.484.51-.297.209-.552.459-.755.73l-.479.641-.479-.641c-.203-.271-.458-.521-.755-.73-.479-.338-.979-.51-1.484-.51z"/>
  </svg>
);

const Footer = () => {
  const { t, language } = useLanguage();
  const { phone, address, getWhatsAppLink } = useBusinessInfo();
  
  return (
    <footer data-testid="footer" className="bg-[#2F3E30] text-white">
      <div className="container-ribba py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <h3 className="font-heading text-2xl font-bold">Cabañas RIBBA</h3>
            <p className="text-white/70 text-sm leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="flex gap-4 pt-2">
              <a
                href="https://www.instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#C6A87C] transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#C6A87C] transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.airbnb.mx/users/profile/1463400275602170606"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#FF5A5F] transition-colors"
                aria-label="Airbnb"
                data-testid="airbnb-link"
              >
                <AirbnbIcon className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Links */}
          <div className="space-y-4">
            <h4 className="font-heading text-lg font-semibold">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-white/70 hover:text-[#C6A87C] transition-colors text-sm">
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link to="/cabanas" className="text-white/70 hover:text-[#C6A87C] transition-colors text-sm">
                  {t('nav.cabins')}
                </Link>
              </li>
              <li>
                <Link to="/reservar" className="text-white/70 hover:text-[#C6A87C] transition-colors text-sm">
                  {t('nav.book')}
                </Link>
              </li>
              <li>
                <Link to="/mazamitla" className="text-white/70 hover:text-[#C6A87C] transition-colors text-sm">
                  {t('nav.mazamitla')}
                </Link>
              </li>
              <li>
                <Link to="/contacto" className="text-white/70 hover:text-[#C6A87C] transition-colors text-sm">
                  {t('nav.contact')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h4 className="font-heading text-lg font-semibold">{t('footer.contact')}</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#C6A87C] flex-shrink-0 mt-0.5" />
                <span className="text-white/70 text-sm">
                  {address}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#C6A87C] flex-shrink-0" />
                <a
                  href={getWhatsAppLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/70 hover:text-[#C6A87C] transition-colors text-sm"
                >
                  {phone}
                </a>
              </li>
            </ul>
          </div>

          {/* Booking CTA */}
          <div className="space-y-4">
            <h4 className="font-heading text-lg font-semibold">
              {language === 'es' ? '¿Listo para escapar?' : 'Ready to escape?'}
            </h4>
            <p className="text-white/70 text-sm">
              {language === 'es' 
                ? 'Reserva tu cabaña hoy y vive la experiencia Mazamitla.' 
                : 'Book your cabin today and live the Mazamitla experience.'}
            </p>
            <Link
              to="/reservar"
              data-testid="footer-reservar-btn"
              className="inline-block px-6 py-3 bg-[#C6A87C] text-[#1A1A1A] font-medium rounded-sm hover:bg-white transition-colors text-sm"
            >
              {t('hero.bookNow')}
            </Link>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/50 text-sm">
            © {new Date().getFullYear()} Cabañas RIBBA. {t('footer.rights')}.
          </p>
          <div className="flex gap-6">
            <Link to="/privacidad" className="text-white/50 hover:text-white/70 text-xs">
              {language === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}
            </Link>
            <Link to="/terminos" className="text-white/50 hover:text-white/70 text-xs">
              {language === 'es' ? 'Términos y Condiciones' : 'Terms and Conditions'}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
